export type Plan = 'starter' | 'growth' | 'business' | 'enterprise'

const PLAN_ORDER: Plan[] = ['starter', 'growth', 'business', 'enterprise']

/** Platform domain for public KB subdomains, e.g. slug.usesondersoft.com */
export function getPlatformKbDomain() {
  return (
    import.meta.env.VITE_KB_PUBLIC_DOMAIN ??
    import.meta.env.VITE_KB_PLATFORM_DOMAIN ??
    'usesondersoft.com'
  )
}

/** @deprecated Use getPlatformKbDomain() */
export function getStarterKbDomain() {
  return getPlatformKbDomain()
}

export function getMaxMembers(plan: string): number | null {
  switch (plan as Plan) {
    case 'starter':
      return 3
    case 'growth':
      return 10
    case 'business':
      return 25
    case 'enterprise':
      return null
    default:
      return 3
  }
}

export function getMaxWorkspaces(plan: string): number | null {
  if (plan === 'enterprise') return null
  return 1
}

export function canHidePoweredBy(plan: string): boolean {
  return PLAN_ORDER.indexOf(plan as Plan) >= PLAN_ORDER.indexOf('growth')
}

/** Enterprise customers can map their own domain, e.g. www.theirdomain.com */
export function canUseCustomDomain(plan: string): boolean {
  return plan === 'enterprise'
}

export function canUseSupportModule(plan: string): boolean {
  return PLAN_ORDER.indexOf(plan as Plan) >= PLAN_ORDER.indexOf('growth')
}

export function canUseCrmModule(plan: string): boolean {
  return PLAN_ORDER.indexOf(plan as Plan) >= PLAN_ORDER.indexOf('business')
}

export function isPlatformKbHost(hostname: string): boolean {
  const host = hostname.split(':')[0].toLowerCase()
  const platformDomain = getPlatformKbDomain()
  if (host === platformDomain) return false
  return host.endsWith(`.${platformDomain}`)
}

export function getPublicKbUrlForPlan(slug: string): string {
  if (import.meta.env.DEV) {
    return `http://${slug}.localhost:5173/knowledge`
  }
  return `https://${slug}.${getPlatformKbDomain()}/knowledge`
}

export function formatPlanLabel(plan: string) {
  return plan.charAt(0).toUpperCase() + plan.slice(1)
}

export const PLAN_PRICING = {
  starter: {
    usd: 'Free',
    ngn: null,
    cta: 'Get started',
    highlighted: false,
  },
  growth: {
    usd: '$19/month',
    ngn: '₦29,000/month',
    ngnAnnual: '₦290,000/yr (save ₦58,000)',
    cta: 'Start free trial',
    trialLabel: '14-day free trial',
    highlighted: true,
  },
  business: {
    usd: '$49/month',
    ngn: '₦75,000/month',
    ngnAnnual: '₦750,000/yr (save ₦150,000)',
    cta: 'Talk to us',
    trialLabel: '14-day free trial',
    highlighted: false,
  },
  enterprise: {
    usd: 'Custom (from $150/month — custom contract)',
    ngn: null,
    cta: 'Contact sales',
    highlighted: false,
  },
} as const

export const PLAN_FEATURES = [
  { label: 'Workspaces', starter: '1', growth: '1', business: '1', enterprise: 'Multiple workspaces — contact us' },
  { label: 'Knowledge module', starter: true, growth: true, business: true, enterprise: true },
  { label: 'Support module', starter: false, growth: true, business: true, enterprise: true },
  {
    label: 'Members',
    starter: '3',
    growth: '10',
    business: '25',
    enterprise: 'Unlimited',
  },
  {
    label: 'Public help center URL',
    starter: '[slug].usesondersoft.com',
    growth: '[slug].usesondersoft.com',
    business: '[slug].usesondersoft.com',
    enterprise: 'Your own domain',
  },
  {
    label: 'Branding',
    starter: 'Powered by shown',
    growth: 'Remove Powered by',
    business: 'Remove Powered by',
    enterprise: 'White label',
  },
  {
    label: 'Support tier',
    starter: 'Community',
    growth: 'Standard',
    business: 'Priority',
    enterprise: 'SLA',
  },
  {
    label: 'CRM early access',
    starter: false,
    growth: false,
    business: true,
    enterprise: true,
  },
] as const

export function formatPlanFeatureValue(value: boolean | string): string {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  return value
}
